import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { BroadcasterModule, PublicBroadcaster } from '@ebu/contracts';
import { buttonClassName } from '@ebu/ui';
import { PageHeading, useDocumentTitle } from '../../app/page';
import { useI18n, type MessageKey } from '../../i18n/i18n';
import type { PlaceId } from '../hub/hub-model';
import { useGuest } from './guest-state';

/**
 * Member white-label (Phase 9, ADR 0015): each broadcaster configuration lists the places it
 * offers. A place that is switched off says so plainly instead of vanishing; nothing the child
 * saved on this browser is removed. While the configuration is loading everything counts as offered.
 */

export const PLACE_MODULE: Partial<Record<PlaceId, BroadcasterModule>> = {
  lab: 'discovery-game',
  workshop: 'world-builder',
};

export const offers = (broadcaster: PublicBroadcaster | null, module: BroadcasterModule) =>
  broadcaster === null || broadcaster.enabledModules.includes(module);

export function useOffers(module: BroadcasterModule): boolean {
  return offers(useGuest().broadcaster, module);
}

export function Offered({
  module,
  place,
  children,
}: {
  module: BroadcasterModule;
  place: MessageKey;
  children: ReactNode;
}) {
  const { broadcaster } = useGuest();
  if (offers(broadcaster, module) || !broadcaster) return children;
  return <NotOffered place={place} broadcaster={broadcaster.displayName} />;
}

function NotOffered({ place, broadcaster }: { place: MessageKey; broadcaster: string }) {
  const { t } = useI18n();
  const title = t('offer.title', { place: t(place), broadcaster });
  useDocumentTitle(t(place));
  return (
    <div className="cinema">
      <div className="cinema__page narrow" data-testid="not-offered">
        <PageHeading>{title}</PageHeading>
        <p className="lead">{t('offer.body')}</p>
        <p>{t('offer.kept')}</p>
        <div className="button-row">
          <Link to="/broadcaster" className={buttonClassName('primary')}>
            {t('offer.globe')}
          </Link>
          <Link to="/" className={buttonClassName('secondary')}>
            {t('offer.town')}
          </Link>
        </div>
      </div>
    </div>
  );
}
